import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'longform', label: '롱폼' },
  { key: 'shortform', label: '숏폼' },
  { key: 'podcast', label: '🎙 팟캐스트' },
];

export default function ScriptLibrary() {
  const { api } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [filter, setFilter] = useState('all');

  const loadScripts = useCallback(async () => {
    try {
      const r = await api.get('/script/list');
      setScripts(r.data.scripts || []);
    } catch (e) {
      addToast('대본 목록 로드 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, addToast]);

  useEffect(() => { loadScripts(); }, [loadScripts]);

  const handleDelete = async (id) => {
    if (!window.confirm('이 대본을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/script/${id}`);
      addToast('대본이 삭제되었습니다', 'success');
      loadScripts();
    } catch (e) {
      addToast(e.response?.data?.error || '삭제 실패', 'error');
    }
  };

  const handleDownload = async (id, type, shortformIdx) => {
    try {
      const token = localStorage.getItem('accessToken');
      let url = `/api/script/download/${id}?type=${type}`;
      if (shortformIdx !== undefined) url += `&shortform=${shortformIdx}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      let filename = type === 'shooting' ? '대본_촬영용.docx' : '대본_AI편집용.json';
      const match = cd.match(/filename\*=UTF-8''(.+)/);
      if (match) filename = decodeURIComponent(match[1]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      addToast('다운로드 완료', 'success');
    } catch (e) {
      addToast('다운로드 실패', 'error');
    }
  };

  const handleEdit = (id) => {
    navigate(`/script?load=${id}`);
  };

  const handleViewPodcast = (id) => navigate(`/podcast/view/${id}`);

  const handleDownloadPodcast = async (id, topic) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/script/podcast/download/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `팟캐스트_${topic || '대본'}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      addToast('다운로드 완료', 'success');
    } catch (e) {
      addToast('다운로드 실패', 'error');
    }
  };

  // 검색 필터
  const searched = scripts.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.keyword || '').toLowerCase().includes(q) ||
           (s.topic?.title || '').toLowerCase().includes(q) ||
           (s.podcastData?.topic || '').toLowerCase().includes(q);
  });

  // 분류
  const podcasts = searched.filter(s => s.scriptType === 'podcast');
  const nonPodcasts = searched.filter(s => s.scriptType !== 'podcast');
  const longforms = nonPodcasts.filter(s => !s.shortforms || s.shortforms.length === 0);
  const shortformItems = [];
  nonPodcasts.forEach(s => {
    if (s.shortforms && s.shortforms.length > 0) {
      s.shortforms.forEach(sf => {
        shortformItems.push({ ...sf, parentId: s._id, keyword: s.keyword, createdAt: s.createdAt });
      });
    }
  });

  // 정렬
  const sortFn = (a, b) => {
    const da = new Date(a.createdAt);
    const db = new Date(b.createdAt);
    return sortOrder === 'newest' ? db - da : da - db;
  };

  const sortedLongforms = [...longforms].sort(sortFn);
  const sortedShortforms = [...shortformItems].sort(sortFn);
  const sortedPodcasts = [...podcasts].sort(sortFn);

  // 카운트
  const longCount = longforms.length;
  const shortCount = shortformItems.length;
  const podcastCount = podcasts.length;
  const allCount = longCount + shortCount + podcastCount;

  const formatDate = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    return `${dt.getFullYear()}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getDate()).padStart(2, '0')}`;
  };

  return (
    <div>
      <h1 style={S.h1}>대본 보관함</h1>
      <p style={S.desc}>저장된 대본을 관리하고 편집을 이어가세요</p>

      {/* 필터 탭 */}
      <div style={S.filterBar}>
        {FILTERS.map(f => (
          <button key={f.key}
            onClick={() => setFilter(f.key)}
            style={{ ...S.filterBtn, ...(filter === f.key ? S.filterBtnActive : {}) }}>
            {f.label}
            {f.key === 'all' ? ` (${allCount})` :
              f.key === 'longform' ? ` (${longCount})` :
              f.key === 'shortform' ? ` (${shortCount})` :
              ` (${podcastCount})`}
          </button>
        ))}
      </div>

      {/* 검색 + 정렬 */}
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <span style={S.searchIcon}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="키워드로 검색..."
            style={S.searchInput}
          />
        </div>
        <select
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          style={S.sortSelect}
        >
          <option value="newest">최신순</option>
          <option value="oldest">오래된순</option>
        </select>
      </div>

      {/* 목록 */}
      {loading ? (
        <div style={S.emptyState}>
          <div style={S.spinner} />
          <p style={{ color: '#8B949E', marginTop: 16 }}>로딩 중...</p>
        </div>
      ) : (
        <>
          {/* 롱폼 카드 */}
          {(filter === 'all' || filter === 'longform') && sortedLongforms.length > 0 && (
            <>
              {filter === 'all' && <h3 style={S.sectionTitle}>롱폼 대본</h3>}
              <div style={S.grid}>
                {sortedLongforms.map(s => (
                  <div key={s._id} style={S.card}>
                    {s.keyword && <span style={S.kwBadge}>{s.keyword}</span>}
                    <h3 style={S.cardTitle}>{s.topic?.title || '제목 없음'}</h3>
                    <p style={S.cardPreview}>
                      {truncate(s.topic?.hook || s.topic?.title || '', 50)}
                    </p>
                    <p style={S.cardDate}>{formatDate(s.createdAt)}</p>
                    <div style={S.cardActions}>
                      <button style={S.actionBtn} onClick={() => handleEdit(s._id)}>이어 편집</button>
                      <button style={S.actionBtn} onClick={() => handleDownload(s._id, 'shooting')}>다운로드</button>
                      <button style={S.actionBtnDanger} onClick={() => handleDelete(s._id)}>삭제</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* 숏폼 카드 */}
          {(filter === 'all' || filter === 'shortform') && sortedShortforms.length > 0 && (
            <>
              {filter === 'all' && <h3 style={{ ...S.sectionTitle, marginTop: 32 }}>숏폼 대본</h3>}
              <div style={S.grid}>
                {sortedShortforms.map((sf, i) => (
                  <div key={`${sf.parentId}-${sf.index}`} style={S.card}>
                    {sf.keyword && <span style={S.kwBadge}>{sf.keyword}</span>}
                    <div style={S.sfHeader}>
                      <span style={S.sfIndex}>숏폼 #{sf.index}</span>
                      {sf.duration && <span style={S.sfDuration}>{sf.duration}</span>}
                    </div>
                    <p style={S.cardPreview}>{truncate(sf.hook || '', 50)}</p>
                    <p style={S.cardDate}>{formatDate(sf.createdAt)}</p>
                    <div style={S.cardActions}>
                      <button style={S.actionBtn} onClick={() => handleDownload(sf.parentId, 'shooting', sf.index)}>
                        촬영용 다운로드
                      </button>
                      <button style={S.actionBtn} onClick={() => handleDownload(sf.parentId, 'editing', sf.index)}>
                        AI편집용 다운로드
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* 팟캐스트 카드 */}
          {(filter === 'all' || filter === 'podcast') && sortedPodcasts.length > 0 && (
            <>
              {filter === 'all' && <h3 style={{ ...S.sectionTitle, marginTop: 32 }}>🎙 팟캐스트 대본</h3>}
              <div style={S.grid}>
                {sortedPodcasts.map(s => {
                  const pd = s.podcastData || {};
                  const hostNames = (pd.hosts || []).map(h => h.name).filter(Boolean).join(', ');
                  return (
                    <div key={s._id} style={S.podcastCard}>
                      <span style={S.podcastBadge}>🎙 팟캐스트</span>
                      <h3 style={S.cardTitle}>{pd.topic || s.topic?.title || '제목 없음'}</h3>
                      <p style={S.cardPreview}>
                        {hostNames ? `출연: ${hostNames}` : ''}
                      </p>
                      <p style={S.podcastMeta}>
                        {pd.duration ? `⏱ ${pd.duration}분` : ''} {pd.tone ? ` · ${pd.tone}` : ''}
                      </p>
                      <p style={S.cardDate}>{formatDate(s.createdAt)}</p>
                      <div style={S.cardActions}>
                        <button style={S.actionBtnAccent} onClick={() => handleViewPodcast(s._id)}>대본 보기</button>
                        <button style={S.actionBtn} onClick={() => handleDownloadPodcast(s._id, pd.topic || s.topic?.title)}>워드</button>
                        <button style={S.actionBtnDanger} onClick={() => handleDelete(s._id)}>삭제</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* 빈 상태 */}
          {filter === 'longform' && sortedLongforms.length === 0 && (
            <div style={S.emptyState}>
              <span style={{ fontSize: 48, opacity: 0.5 }}>📄</span>
              <p style={{ color: '#8B949E', marginTop: 12 }}>
                {search ? '검색 결과가 없습니다' : '저장된 롱폼 대본이 없습니다'}
              </p>
              {!search && (
                <button style={S.primaryBtn} onClick={() => navigate('/script')}>대본 생성하러 가기</button>
              )}
            </div>
          )}

          {filter === 'shortform' && sortedShortforms.length === 0 && (
            <div style={S.emptyState}>
              <span style={{ fontSize: 48, opacity: 0.5 }}>🎬</span>
              <p style={{ color: '#8B949E', marginTop: 12 }}>
                {search ? '검색 결과가 없습니다' : '저장된 숏폼 대본이 없습니다'}
              </p>
            </div>
          )}

          {filter === 'podcast' && sortedPodcasts.length === 0 && (
            <div style={S.emptyState}>
              <span style={{ fontSize: 48, opacity: 0.5 }}>🎙</span>
              <p style={{ color: '#8B949E', marginTop: 12 }}>
                {search ? '검색 결과가 없습니다' : '저장된 팟캐스트 대본이 없습니다'}
              </p>
              {!search && (
                <button style={S.primaryBtn} onClick={() => navigate('/podcast/setup')}>팟캐스트 만들러 가기</button>
              )}
            </div>
          )}

          {filter === 'all' && sortedLongforms.length === 0 && sortedShortforms.length === 0 && sortedPodcasts.length === 0 && (
            <div style={S.emptyState}>
              <span style={{ fontSize: 48, opacity: 0.5 }}>📑</span>
              <p style={{ color: '#8B949E', marginTop: 12 }}>
                {search ? '검색 결과가 없습니다' : '저장된 대본이 없습니다'}
              </p>
              {!search && (
                <button style={S.primaryBtn} onClick={() => navigate('/script')}>대본 생성하러 가기</button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

const S = {
  h1: { color: '#E6EDF3', fontSize: 24, fontWeight: 700, marginBottom: 8 },
  desc: { color: '#8B949E', fontSize: 14, marginBottom: 24 },

  /* 필터 탭 */
  filterBar: { display: 'flex', gap: 8, marginBottom: 16 },
  filterBtn: { padding: '10px 20px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif", fontWeight: 500 },
  filterBtnActive: { borderColor: '#4CAF50', color: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)' },

  toolbar: { display: 'flex', gap: 12, marginBottom: 24 },
  searchWrap: { flex: 1, position: 'relative' },
  searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none' },
  searchInput: { width: '100%', padding: '10px 14px 10px 40px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#E6EDF3', fontSize: 14, outline: 'none', fontFamily: "'Noto Sans KR',sans-serif", boxSizing: 'border-box' },
  sortSelect: { padding: '10px 14px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#E6EDF3', fontSize: 14, outline: 'none', fontFamily: "'Noto Sans KR',sans-serif", cursor: 'pointer' },

  sectionTitle: { color: '#E6EDF3', fontSize: 16, fontWeight: 600, marginBottom: 12 },

  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  card: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 20, display: 'flex', flexDirection: 'column', gap: 8 },
  kwBadge: { alignSelf: 'flex-start', backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  cardTitle: { color: '#E6EDF3', fontSize: 16, fontWeight: 600, margin: 0, lineHeight: 1.4 },
  cardPreview: { color: '#8B949E', fontSize: 13, margin: 0, lineHeight: 1.5, flex: 1 },
  cardDate: { color: '#6E7681', fontSize: 12, margin: 0 },
  cardActions: { display: 'flex', gap: 8, marginTop: 4 },
  actionBtn: { flex: 1, padding: '8px 0', backgroundColor: 'transparent', border: '1px solid #2A3441', borderRadius: 8, color: '#C9D1D9', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  actionBtnDanger: { padding: '8px 12px', backgroundColor: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#EF4444', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },

  /* 숏폼 카드 */
  sfHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  sfIndex: { color: '#7C3AED', fontWeight: 700, fontSize: 15 },
  sfDuration: { backgroundColor: 'rgba(124,58,237,0.15)', color: '#A78BFA', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },

  /* 팟캐스트 카드 */
  podcastCard: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', borderLeft: '3px solid #FF6B35', padding: 20, display: 'flex', flexDirection: 'column', gap: 8 },
  podcastBadge: { alignSelf: 'flex-start', backgroundColor: 'rgba(255,107,53,0.15)', color: '#FF6B35', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  podcastMeta: { color: '#6E7681', fontSize: 12, margin: 0 },
  actionBtnAccent: { flex: 1, padding: '8px 0', backgroundColor: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.4)', borderRadius: 8, color: '#FF6B35', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 0' },
  primaryBtn: { marginTop: 16, padding: '12px 32px', backgroundColor: '#2E7D32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  spinner: { width: 48, height: 48, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
